/**
 * ÇEVRİMDIŞI OKUMA ÖNBELLEĞİNİN KAPSAMI
 *
 * ================================================================
 * NEDEN AYRI DOSYA — KURAL İKİ SEBEPLE ÖLÜYDÜ
 * ================================================================
 * `vite.config.ts` içinde şu yazıyordu:
 *
 *     urlPattern: /^\/api\/v1\/(program|gorev)/
 *
 * 04.09.2026'da ölçüldü, iki ayrı kusur birden:
 *
 * 1. **Başa sabitlenmiş düzenli ifade.** Workbox `RegExpRoute`
 *    kalıba `url.href`i — yani `https://.../api/v1/...` tam adresini —
 *    veriyor. `^\/api` bir tam adresin başıyla asla eşleşmez. Yani
 *    kural ölüydü: hiçbir uç önbelleklenmiyordu.
 * 2. **Yol öneki de yanlıştı.** Kalıp `gorev` diyor; görev uçları
 *    `calisma` denetleyicisinde (`/api/v1/calisma/ogrenci/:id/bugun`).
 *    Birinci kusur düzeltilse bile öğrencinin GÖREVLERİ yine
 *    önbelleklenmezdi.
 *
 * Sonuç: "öğrenci internetsizken programını görebilmeli" (tanıtım
 * §18) sözü tutulmuyordu. Kabuk çevrimdışı açılıyordu (o ön-önbellekten
 * geliyor), görev listesi boş kalıyordu. Kimse fark etmedi çünkü ölü
 * bir yönlendirme kuralı hata üretmez, yalnızca hiçbir şey yapmaz.
 *
 * Kural buraya alındı ki **sınanabilsin**: yapılandırma dosyasının
 * içinde hiçbir testin göremeyeceği bir yerdeydi.
 */

import type { Ben } from "./api";
import { kimlikUret } from "./cevrimdisi/kimlik";
import { oturum } from "./oturum-durumu";

/**
 * Çevrimdışı okuma önbelleğinin adı.
 *
 * `oturumluOku` ile çıkış işlemi AYNI ad önekini kullanmak zorunda:
 * ikisi ayrı ayrı yazılsaydı, ad değiştiğinde çıkış önbelleği
 * temizlemeyi bırakır ve bunu hiçbir şey söylemezdi.
 *
 * 14.09.2026: ortak Workbox önbelleği kullanıcı ayırmadığı için kişisel
 * yanıtlar istemcide rastgele okuma kimliğiyle saklanıyor. Bu ad eski
 * ortak önbelleği de çıkışta temizlemek için korunuyor.
 */
export const OKUMA_ONBELLEGI = "program-verisi";

/**
 * Öğrencinin çevrimdışıyken görebilmesi gereken okuma uçlarının önekleri.
 *
 * ================================================================
 * `/ogrenciler/` ÖNCE DIŞARIDAYDI VE BÜTÜN ÇEVRİMDIŞI OKUMAYI ÖLDÜRÜYORDU
 * ================================================================
 * İlk hâlinde yalnızca `calisma` ve `program` vardı ve
 * `pwa-onbellek.test.ts` `/api/v1/ogrenciler/ben`in önbelleğe
 * girMEMEsini ayrıca iddia ediyordu. Gerekçesi yazılı değildi; oturum
 * durumu (`/auth/ben`) ile aynı kefeye konmuştu.
 *
 * Üretim yapısında canlı ölçüldü (04.09.2026): önbellek doluydu,
 * görev ucu çevrimdışı **200** dönüyordu, ama ekran hâlâ boştu.
 * Sebep `Uygulama.gorevleriYukle`in sırası:
 *
 *     const kayit = await kendiKaydim();          // /ogrenciler/ben
 *     setGorevler(await bugunkuGorevler(kayit.id));
 *
 * İlk çağrı önbellek kapsamı dışında olduğu için düşüyor, `catch`
 * çalışıyor ve **ikinci çağrı hiç yapılmıyor.** Yani önbellekteki
 * görevler hiç istenmiyordu.
 *
 * Kimlik ucunu dışarıda bırakmak bir GÜVENLİK kararı değildi:
 * yetkilendirmeyi her istekte sunucu yapıyor, önbellek yalnızca
 * öğrencinin KENDİ cihazındaki kendi kaydı ve çıkışta düşüyor.
 * `/auth/ben`in dışarıda kalması ise hâlâ doğru — o "oturumum açık
 * mı" sorusu ve bayat bir "evet" çıkış yapmış öğrenciye uygulamayı
 * açık gösterirdi.
 *
 * 14.09.2026: auth yanıtı HTTP önbelleğine hâlâ girmez; son doğrulanmış
 * kimlik soğuk açılış için ayrıca saklanır ve oturum sınırında silinir.
 */
export const CEVRIMDISI_OKUNABILIR_ONEKLER = [
  // Görevler ve çalışma sayacı — `gorev.controller.ts`, path: "calisma"
  "/api/v1/calisma/",
  // Program değişiklik hakkı ve talepleri — path: "program", "program/degisiklik"
  "/api/v1/program/",
  /*
    Öğrencinin kendi kaydı ve kendi müfredat konuları. Görev
    yüklemesinin ÖN KOŞULU; bu olmadan öteki ikisi hiç istenmiyor.
  */
  "/api/v1/ogrenciler/",
] as const;

/**
 * Workbox yönlendirme kalıbı.
 *
 * `url.pathname` üzerinden çalışıyor: düzenli ifade kullanılsaydı
 * href/pathname tuzağı yeniden kurulabilirdi. Fonksiyon hiçbir dış
 * değere kapanmıyor — `generateSW` onu kaynak metnine çevirip
 * `sw.js`e gömüyor, kapanış olsaydı üretilen dosya bozuk olurdu.
 */
export function cevrimdisiOnbellegeGirer({ url }: { url: URL }): boolean {
  /*
    ÖNEKLER BURADA ELLE YAZILI, `CEVRIMDISI_OKUNABILIR_ONEKLER`
    KULLANILMIYOR — ve bu bilinçli. `generateSW` bu fonksiyonu KAYNAK
    METNİNE çevirip `sw.js`e gömüyor; diziye kapansaydı üretilen
    dosyada tanımsız bir değişkene başvururdu ve servis çalışanı
    çalışma anında patlardı. Dosyanın kendi açılış notu bunu zaten
    söylüyor.

    Bedeli iki yerde duran bir liste; `pwa-onbellek.test.ts` ikisinin
    örtüştüğünü ölçüyor, yani ayrışma sessiz kalamıyor.
  */
  return (
    url.pathname.startsWith("/api/v1/calisma/") ||
    url.pathname.startsWith("/api/v1/program/") ||
    url.pathname.startsWith("/api/v1/ogrenciler/")
  );
}

const OTURUM_ANAHTARI = "ogrenci-dogrulanmis-oturum";
let okumaKimligi: string | null = null;

// F-STV-11 — 14.09.2026'da kalıcı depoya taşındı: kapatılan PWA'nın
// sessionStorage'ı sabah internetsiz açılışta yoktur. Çıkış ise kalıcıdır;
// React temizliğini beklemek uygulama hemen kapatılırsa kimliği bırakırdı.
oturum.dinle(() => {
  okumaKimligi = null;
  try {
    localStorage.removeItem(OTURUM_ANAHTARI);
    sessionStorage.removeItem(OTURUM_ANAHTARI);
  } catch {
    /* Saklama kapalıysa bellekteki sınır yine kapanır. */
  }
});

function saklanan(): { ben: Ben; kimlik: string; bildirim: string | null } | null {
  try {
    const ham = localStorage.getItem(OTURUM_ANAHTARI);
    if (!ham) return null;
    const veri = JSON.parse(ham) as { ben: Ben; kimlik: string; bildirim: string | null };
    if (
      !veri.ben?.girisYapildi ||
      !veri.ben.userId ||
      typeof veri.kimlik !== "string" ||
      veri.bildirim !== localStorage.getItem("koclukpanel-oturum-degisimi")
    )
      return null;
    return veri;
  } catch {
    return null;
  }
}

export function cevrimdisiOturum(): Ben | null {
  if (oturum.kapali) return null;
  const veri = saklanan();
  if (!veri) return null;
  okumaKimligi = veri.kimlik;
  oturum.dogrula(veri.ben);
  return veri.ben;
}

export async function okumaOturumunuDogrula(ben: Ben): Promise<void> {
  const nesil = oturum.nesil;
  const onceki = saklanan();
  if (!ben.girisYapildi || !ben.userId) {
    await okumaOnbellegiTemizle();
    return;
  }
  if (onceki?.ben.userId !== ben.userId) await okumaOnbellegiTemizle();
  if (nesil !== oturum.nesil || oturum.kapali) return;
  okumaKimligi ??= onceki?.ben.userId === ben.userId ? onceki.kimlik : kimlikUret();
  try {
    localStorage.setItem(
      OTURUM_ANAHTARI,
      JSON.stringify({
        ben,
        kimlik: okumaKimligi,
        bildirim: localStorage.getItem("koclukpanel-oturum-degisimi"),
      }),
    );
  } catch {
    /* F-STV-11 — saklama kapalıysa yalnız açık sekme devam edebilir. */
  }
}

/**
 * ÇIKIŞTA ÖNBELLEK DÜŞÜYOR — PAYLAŞILAN TELEFON
 *
 * Bu sistem çocukların kişisel verisini tutuyor ve öğrenci
 * uygulaması telefonda kullanılıyor. Önbellek düşmezse, aynı
 * telefondan giriş yapan İKİNCİ öğrenci çevrimdışıyken birincinin
 * programını okuyabilirdi: `NetworkFirst` ağ yoksa önbellekten
 * veriyor ve önbellek kime ait olduğunu bilmiyor.
 *
 * 14.09.2026: NetworkFirst yerine NetworkOnly kullanılıyor; kişisel
 * önbelleği oturum neslini bilen istemci yönetiyor. Kalıcı kimlik çıkış,
 * 401, hesap değişimi ve sekme bildirimiyle hemen düşer. Geç yanıt eski
 * önbelleği yeniden açamaz.
 *
 * Kural ölüyken (04.09.2026'dan önce) hiçbir veri saklanmadığı için
 * sorun da yoktu; kuralı canlandıran değişikliğin parçası.
 */
export async function okumaOnbellegiTemizle(): Promise<void> {
  // F-STV-11 — kimlik önce düşer; eski istek temizlenen önbelleği diriltemez.
  okumaKimligi = null;
  try {
    localStorage.removeItem(OTURUM_ANAHTARI);
    sessionStorage.removeItem(OTURUM_ANAHTARI);
  } catch {
    /* Saklama kapalı olabilir. */
  }
  if (typeof caches === "undefined") return;
  try {
    const adlar = await caches.keys();
    await Promise.all(
      adlar
        .filter((ad) => ad === OKUMA_ONBELLEGI || ad.startsWith(`${OKUMA_ONBELLEGI}-`))
        .map((ad) => caches.delete(ad)),
    );
  } catch {
    /* F-AUTH-07 — eski anahtar bırakıldığı için silinemeyen veri yeni hesaba açılmaz. */
  }
}

/**
 * ÖNBELLEKTEN OKUNDU MU — UI/UX Dalga 1 (mutabakat Ö9/G-31).
 *
 * `navigator.onLine` arayüz açıkken `true` kalıyor (otel wifi'si, sunucuya
 * ulaşamayan ağ); o anda aşağıdaki geri dönüş sessizce önbelleği veriyordu
 * ve ekran "her şey güncel" görünüyordu — kanıtta öğrenci çevrimdışıyken
 * dolu bir Bugün gördü, hiçbir uyarı yoktu. Olay, kabuktaki şeridin
 * "son kaydedilen bilgiler" demesini sağlıyor; ağdan ilk yanıtta söner.
 */
export const ONBELLEK_OLAYI = "onbellekten-okuma";

function onbellekDurumuBildir(onbellekten: boolean): void {
  window.dispatchEvent(new CustomEvent<boolean>(ONBELLEK_OLAYI, { detail: onbellekten }));
}

export async function oturumluOku(
  adres: string,
  secenekler: RequestInit,
  onbellekKullan = true,
): Promise<Response> {
  if (!onbellekKullan) return fetch(adres, secenekler);
  if (typeof window === "undefined") return fetch(adres, secenekler);
  const nesil = oturum.nesil;
  const kimlik = okumaKimligi;
  const url = new URL(adres, window.location.origin);
  const uygun = secenekler.method === "GET" && cevrimdisiOnbellegeGirer({ url });
  if (!uygun || !kimlik || !oturum.kullaniciId || typeof caches === "undefined")
    return fetch(adres, secenekler);
  // F-AUTH-07 — 14.09.2026'da anahtar ağ adresinden ayrıldı: okuma kimliği
  // sunucu günlüklerine ve sorgu şemasına taşınmamalı. Bu sentetik URL yalnız
  // Cache API'ye verilir; fetch özgün adresle NetworkOnly kuralından geçer.
  url.searchParams.set("__oturum", kimlik);
  const onbellekAdi = `${OKUMA_ONBELLEGI}-${kimlik}`;
  const gecerli = () =>
    nesil === oturum.nesil && kimlik === okumaKimligi && !secenekler.signal?.aborted;
  try {
    const yanit = await fetch(adres, secenekler);
    // Yanıt geldiyse ağ çalışıyor — hata kodlu olsa bile önbellek kipi biter.
    onbellekDurumuBildir(false);
    if (yanit.ok && gecerli()) {
      try {
        const onbellek = await caches.open(onbellekAdi);
        if (gecerli()) {
          const kopya = yanit.clone();
          const basliklar = new Headers(kopya.headers);
          basliklar.set("X-Yerel-Kayit-Zamani", String(Date.now()));
          await onbellek.put(
            url.href,
            new Response(await kopya.arrayBuffer(), { status: kopya.status, headers: basliklar }),
          );
          const anahtarlar = await onbellek.keys();
          await Promise.all(
            anahtarlar
              .slice(0, Math.max(0, anahtarlar.length - 60))
              .map((anahtar) => onbellek.delete(anahtar)),
          );
        }
        if (!gecerli()) await caches.delete(onbellekAdi);
      } catch {
        /* F-STV-11 — kota dolması başarılı ağ yanıtını kaybettirmemeli. */
      }
    }
    return yanit;
  } catch (hata) {
    if (gecerli()) {
      const onbellek = await caches.open(onbellekAdi);
      const kayit = await onbellek.match(url.href);
      if (
        kayit &&
        gecerli() &&
        Date.now() - Number(kayit.headers.get("X-Yerel-Kayit-Zamani")) < 7 * 24 * 60 * 60_000
      ) {
        onbellekDurumuBildir(true);
        return kayit;
      }
    }
    throw hata;
  }
}
